import type { ResearchDelivery } from './delivery';
import { REPLAY_FRAME_MAX_NODES, REPLAY_FRAME_MAX_TEXT, REPLAY_ROUTES, type ReplayElementNode, type ReplayFrameInput, type ReplayNode, type ReplayRoute } from '../../../server/src/research/replay-schema.js';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PRIVATE_CLASS = /(?:^|[-_ ])(?:password|credential|secret|token|invite|invitation|recovery|auth)(?:$|[-_ ])/i;
const OPAQUE_TOKEN = /\b[A-Za-z0-9_-]{20,}\b/g;
const TOKEN_PATH = /\/(?:join|recover|friend|circle\/join)\/[^\s/?#]+/gi;
const TOKEN_QUERY = /([?&#](?:token|secret|code|invite|invitation|recovery)=)[^&#\s]+/gi;
const SKIP_TAGS = new Set(['AUDIO', 'VIDEO', 'CANVAS', 'IFRAME', 'OBJECT', 'EMBED', 'SCRIPT', 'NOSCRIPT', 'INPUT', 'SELECT']);
const SAFE_ATTRS = new Set(['aria-label', 'aria-labelledby', 'aria-modal', 'aria-pressed', 'aria-current', 'aria-expanded', 'aria-selected', 'class', 'checked', 'cx', 'cy', 'd', 'dir', 'disabled', 'fill', 'fill-rule', 'height', 'id', 'lang', 'points', 'r', 'role', 'selected', 'stroke', 'stroke-linecap', 'stroke-linejoin', 'stroke-width', 'tabindex', 'transform', 'viewbox', 'width', 'x', 'y']);
const UNSAFE_CSS = /(?:url\s*\(|@import|expression\s*\(|javascript:|behavior\s*:)/i;

export interface ReplayControlStatus {
  eligible: boolean;
  enabled: boolean;
  active: boolean;
  unavailableReason?: string;
  retentionDays: number;
  destination: string;
  region: string | null;
  generation?: string;
}

type FrameSender = typeof fetch;

function uuid(): string {
  if (typeof crypto === 'undefined' || !crypto.randomUUID) return '';
  return crypto.randomUUID();
}

function routeForPage(path: string): ReplayRoute | null {
  if (path === '/') return 'home';
  if (path === '/record') return 'capture';
  if (path === '/round' || path === '/review') return 'round';
  if (/^\/moment\/[A-Za-z0-9_-]{1,80}$/.test(path)) return 'moment';
  if (/^\/land\/[A-Za-z0-9_-]{1,80}$/.test(path)) return 'landing';
  if (/^\/practice\/[A-Za-z0-9_-]{1,80}$/.test(path) || path.startsWith('/tend/')) return 'exercise';
  return null;
}

function visible(element: Element): boolean {
  if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') return false;
  const style = getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && element.getClientRects().length > 0;
}

function attrsOf(element: Element): Record<string, string> | null {
  const attrs: Record<string, string> = {};
  for (const attr of Array.from(element.attributes)) {
    const name = attr.name.toLowerCase();
    if (!SAFE_ATTRS.has(name)) continue;
    if (name === 'class' && PRIVATE_CLASS.test(attr.value)) return null;
    if (['fill', 'stroke'].includes(name) && /url\s*\(/i.test(attr.value)) continue;
    attrs[name] = redactVisibleText(attr.value.slice(0, 2_000));
  }
  return attrs;
}

function redactVisibleText(value: string): string {
  return value.replace(TOKEN_PATH, (match) => `${match.slice(0, match.lastIndexOf('/') + 1)}[redacted]`)
    .replace(TOKEN_QUERY, '$1[redacted]')
    .replace(OPAQUE_TOKEN, '[redacted]');
}

/** Serialize visible DOM only. Form controls, media, network state and non-app routes never enter a frame. */
export function visibleReplayTree(documentRoot?: Document | null): ReplayNode | null {
  const root = documentRoot ?? (typeof document === 'undefined' ? null : document);
  if (!root) return null;
  let nodes = 0;
  let textLength = 0;
  const walk = (node: Node, depth: number): ReplayNode | null => {
    if (++nodes > REPLAY_FRAME_MAX_NODES || depth > 48) return null;
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? '';
      textLength += text.length;
      if (textLength > REPLAY_FRAME_MAX_TEXT) return null;
      return { kind: 'text', text: redactVisibleText(text) };
    }
    if (!(node instanceof Element)) return null;
    const element = node;
    if (element.tagName === 'STYLE') {
      const css = element.textContent ?? '';
      return css.length <= 32_000 && !UNSAFE_CSS.test(css) ? { kind: 'element', tag: 'style', attrs: {}, children: [{ kind: 'text', text: css }] } : null;
    }
    if (SKIP_TAGS.has(element.tagName) || element.matches('[data-replay-exclude], .replay-private') || !visible(element)) return null;
    if (element.matches('textarea')) {
      if (!element.matches('textarea.note')) return null;
      const attrs = attrsOf(element);
      if (!attrs) return null;
      attrs.class = `${attrs.class ?? ''} note-replay`.trim();
      const value = (element as HTMLTextAreaElement).value;
      textLength += value.length;
      if (textLength > REPLAY_FRAME_MAX_TEXT) return null;
      return { kind: 'element', tag: 'div', attrs, children: [{ kind: 'text', text: redactVisibleText(value) }] };
    }
    const attrs = attrsOf(element);
    if (!attrs) return null;
    const children: ReplayNode[] = [];
    for (const child of Array.from(element.childNodes)) {
      const copied = walk(child, depth + 1);
      if (copied) children.push(copied);
    }
    let shadow: ReplayNode[] | undefined;
    if (element.shadowRoot) {
      shadow = [];
      for (const child of Array.from(element.shadowRoot.childNodes)) {
        const copied = walk(child, depth + 1);
        if (copied) shadow.push(copied);
      }
    }
    const result: ReplayElementNode = { kind: 'element', tag: element.tagName.toLowerCase(), attrs, children };
    if (shadow) result.shadow = shadow;
    return result;
  };
  const body = root.body;
  if (!body) return null;
  const tree = walk(body, 0);
  // Playback starts at body; keep the language and direction normally inherited from html.
  if (tree?.kind === 'element') {
    for (const name of ['lang', 'dir']) {
      const value = body.getAttribute(name) ?? root.documentElement.getAttribute(name);
      if (value) tree.attrs[name] = redactVisibleText(value.slice(0, 2_000));
    }
  }
  return tree;
}

function isSensitiveRoute(path: string): boolean {
  return path === '/signin' || path.startsWith('/join/') || path.startsWith('/recover/') || path.startsWith('/friend/') ||
    path.startsWith('/circle/join/') || path.startsWith('/api/') || path.startsWith('/prototype/') || path.startsWith('/admin/') ||
    path.startsWith('/tutor/');
}

/** Participant-controlled same-origin DOM snapshot adapter. No microphone or desktop APIs are used. */
export class ReplayAdapter {
  private statusValue: ReplayControlStatus = { eligible: false, enabled: false, active: false, retentionDays: 30, destination: 'same-origin', region: null };
  private subject = '';
  private lifecycle = 0;
  private orphanAttemptId = '';
  private sequence = 0;
  private attemptFrames = 0;
  private observer: MutationObserver | null = null;
  private captureTimer = 0;
  private lastCaptureAt = 0;
  private pending = new Set<Promise<unknown>>();
  private readonly fetcher: FrameSender;
  private readonly channel: BroadcastChannel | null;
  private readonly onBroadcast = (event: MessageEvent): void => {
    const data = event.data as { type?: unknown; subjectId?: unknown } | null;
    if (data?.type !== 'stop' || data.subjectId !== this.subject) return;
    this.markStopped(this.subject);
    this.stopLocal();
    this.statusValue.active = false;
    this.research.setReplayActiveLocal(false);
    this.dispatch();
  };
  private readonly onStorage = (event: StorageEvent): void => {
    if (!this.subject || event.key !== this.stopKey(this.subject) || event.newValue !== '1') return;
    this.stopLocal();
    this.statusValue.active = false;
    this.research.setReplayActiveLocal(false);
    this.dispatch();
  };

  /** Semantic research ending anywhere (the default notice's Stop, another screen) ends capture here too. */
  private readonly onResearchState = (): void => {
    if (this.research.status.active || (!this.observer && !this.statusValue.active)) return;
    this.endCapture();
  };

  private readonly doc: Document | null;

  constructor(private readonly research: ResearchDelivery, fetcher: FrameSender = fetch.bind(globalThis), doc?: Document | null) {
    this.fetcher = fetcher;
    this.doc = doc === undefined ? (typeof document === 'undefined' ? null : document) : doc;
    try { this.channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('li-replay-stop-v1') : null; }
    catch { this.channel = null; }
    this.channel?.addEventListener('message', this.onBroadcast);
    // Node's BroadcastChannel keeps the process alive; importing research-client under node:test must not hang it.
    (this.channel as { unref?: () => void } | null)?.unref?.();
    this.doc?.defaultView?.addEventListener('storage', this.onStorage);
    this.doc?.defaultView?.addEventListener('li-research-state', this.onResearchState);
  }

  /** Stop capturing in this tab without a local stop marker: the server's state stays the source of truth. */
  private endCapture(): void {
    this.stopLocal();
    this.statusValue.active = false;
    this.research.setReplayActiveLocal(false);
    this.dispatch();
  }

  get status(): ReplayControlStatus { return this.statusValue; }

  async start(): Promise<ReplayControlStatus> {
    const replay = this.research.status.replay;
    const subject = this.research.status.subjectId ?? '';
    if (!replay || !subject || !this.research.status.active) {
      this.stopLocal();
      this.subject = '';
      this.dispatch();
      return this.statusValue;
    }
    if (this.subject && this.subject !== subject) this.suspend();
    this.subject = subject;
    this.statusValue = { ...replay, active: replay.enabled && replay.active };
    if (this.hasLocalStop(subject)) {
      // setReplayActiveLocal notifies only on change (see delivery.ts), so a
      // redundant call while the stop marker stands is already a no-op: one
      // start() is one dispatch without a second guard here.
      this.statusValue.active = false;
      this.stopLocal();
      this.research.setReplayActiveLocal(false);
    } else if (this.statusValue.eligible && this.statusValue.enabled && this.statusValue.active) {
      this.startLocal();
    }
    this.dispatch();
    return this.statusValue;
  }

  async optIn(): Promise<boolean> {
    const lifecycle = this.lifecycle;
    if (!this.statusValue.eligible || !this.statusValue.enabled || !this.research.status.active || !this.subject || !this.storageReady()) return false;
    try {
      const response = await this.fetcher('/api/research/replay/participation', {
        method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'sec-fetch-site': 'same-origin', 'X-Research-Subject': this.subject }, body: '{}',
      });
      if (!response.ok || lifecycle !== this.lifecycle) return false;
      const result = await response.json().catch(() => null) as { generation?: unknown } | null;
      if (!result || typeof result.generation !== 'string' || !UUID.test(result.generation) || lifecycle !== this.lifecycle) return false;
      if (!this.clearLocalStop(this.subject)) return false;
      this.statusValue.generation = result.generation;
      this.statusValue.active = true;
      this.research.setReplayActiveLocal(true, result.generation);
      this.startLocal();
      this.dispatch();
      return true;
    } catch { return false; }
  }

  /** Pause future collection while retaining the frames already consented to. */
  async stop(): Promise<boolean> {
    const subject = this.subject;
    const lifecycle = this.lifecycle;
    if (subject) this.markStopped(subject);
    this.stopLocal();
    this.statusValue.active = false;
    this.research.setReplayActiveLocal(false);
    this.dispatch();
    this.broadcastStop(subject);
    await this.flushPending();
    if (!subject) return false;
    try {
      const response = await this.fetcher('/api/research/replay/stop', {
        method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'sec-fetch-site': 'same-origin', 'X-Research-Subject': subject }, body: '{}',
      });
      return response.ok && lifecycle === this.lifecycle;
    } catch { return false; }
  }

  /** Clear prior frames, then begin a fresh recording while consent remains active. */
  async reset(): Promise<boolean> {
    if (!this.statusValue.active || !this.subject) return false;
    this.stopLocal();
    await this.flushPending();
    const lifecycle = this.lifecycle;
    try {
      const response = await this.fetcher('/api/research/replay/reset', {
        method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'sec-fetch-site': 'same-origin', 'X-Research-Subject': this.subject }, body: '{}',
      });
      if (!response.ok || lifecycle !== this.lifecycle) return false;
      const result = await response.json().catch(() => null) as { generation?: unknown } | null;
      if (!result || typeof result.generation !== 'string' || !UUID.test(result.generation)) return false;
      this.orphanAttemptId = '';
      this.sequence = 0;
      this.attemptFrames = 0;
      this.statusValue.generation = result.generation;
      this.research.setReplayActiveLocal(true, result.generation);
      this.startLocal();
      return true;
    } catch { return false; }
  }

  /** Opt out synchronously in this tab, then ask the server to stop and delete all replay frames. */
  async optOut(): Promise<boolean> {
    const subject = this.subject;
    const lifecycle = this.lifecycle;
    this.stopLocal();
    this.statusValue.active = false;
    this.research.setReplayActiveLocal(false);
    if (subject) this.markStopped(subject);
    this.dispatch();
    this.broadcastStop(subject);
    await this.flushPending();
    const ok = await this.deleteServerParticipation(subject, lifecycle);
    if (ok && subject) this.clearLocalStop(subject);
    return ok;
  }

  /** Account changes stop observers and invalidate pending callbacks before the new session starts. */
  suspend(): void {
    this.lifecycle++;
    this.stopLocal();
    this.subject = '';
    this.orphanAttemptId = '';
    this.sequence = 0;
    this.attemptFrames = 0;
    this.currentAttemptId = '';
    this.statusValue = { ...this.statusValue, active: false };
    this.research.setReplayActiveLocal(false);
    this.dispatch();
  }

  private startLocal(): void {
    if (!this.doc || !this.statusValue.active || !this.subject || this.observer || !this.statusValue.enabled ||
        !this.statusValue.generation || !UUID.test(this.statusValue.generation)) return;
    this.orphanAttemptId = uuid();
    if (!this.orphanAttemptId) return;
    const generation = this.lifecycle;
    const doc = this.doc;
    if (!doc) return;
    const capture = () => {
      if (generation !== this.lifecycle || !this.statusValue.active) return;
      const path = doc.defaultView?.location.pathname ?? '';
      if (isSensitiveRoute(path)) return;
      const route = routeForPage(path);
      if (!route || !(REPLAY_ROUTES as readonly string[]).includes(route)) return;
      const now = Date.now();
      if (now - this.lastCaptureAt < 900) return;
      this.lastCaptureAt = now;
      const tree = visibleReplayTree(doc);
      if (!tree) return;
      const attempt = this.research.currentAttempt();
      const attemptId = (attempt?.attemptId ?? this.currentAttemptId) || this.orphanAttemptId;
      if (!UUID.test(attemptId)) return;
      if (this.currentAttemptId !== attemptId) {
        this.currentAttemptId = attemptId;
        this.attemptFrames = 0;
      }
      if (this.attemptFrames >= 600) return;
      this.attemptFrames++;
      const view = doc.defaultView;
      const frame: ReplayFrameInput = {
        sessionId: attempt?.sessionId ?? this.research.sessionId(), attemptId, generation: this.statusValue.generation!,
        sequence: this.sequence++, capturedAt: new Date(now).toISOString(), route,
        viewport: { width: Math.max(1, Math.min(4_000, view?.innerWidth ?? 1)), height: Math.max(1, Math.min(4_000, view?.innerHeight ?? 1)) },
        tree,
      };
      this.sendFrame(frame, generation, this.subject);
    };
    const schedule = () => {
      if (this.captureTimer) window.clearTimeout(this.captureTimer);
      this.captureTimer = window.setTimeout(() => { this.captureTimer = 0; capture(); }, 1_000);
    };
    const observers = new WeakSet<Node>();
    const observeRoot = (root: Document | ShadowRoot) => {
      if (observers.has(root)) return;
      observers.add(root);
      const observer = new MutationObserver((records) => {
        for (const record of records) for (const node of Array.from(record.addedNodes)) {
          if (node instanceof Element) {
            if (node.shadowRoot) observeRoot(node.shadowRoot);
            for (const child of Array.from(node.querySelectorAll('*'))) if (child.shadowRoot) observeRoot(child.shadowRoot);
          }
        }
        schedule();
      });
      observer.observe(root, { subtree: true, childList: true, attributes: true, characterData: true });
      if (!this.observer) this.observer = observer;
      else this.extraObservers.push(observer);
      for (const element of Array.from(root.querySelectorAll('*'))) if (element.shadowRoot) observeRoot(element.shadowRoot);
    };
    this.extraObservers = [];
    observeRoot(doc);
    doc.addEventListener('input', schedule, true);
    doc.addEventListener('change', schedule, true);
    doc.defaultView?.addEventListener('popstate', schedule);
    this.cleanupListeners = () => {
      doc.removeEventListener('input', schedule, true);
      doc.removeEventListener('change', schedule, true);
      doc.defaultView?.removeEventListener('popstate', schedule);
    };
    capture();
    this.dispatch();
  }

  private currentAttemptId = '';
  private extraObservers: MutationObserver[] = [];
  private cleanupListeners: (() => void) | null = null;

  private stopLocal(): void {
    if (this.captureTimer) window.clearTimeout(this.captureTimer);
    this.captureTimer = 0;
    this.observer?.disconnect();
    this.observer = null;
    for (const observer of this.extraObservers) observer.disconnect();
    this.extraObservers = [];
    this.cleanupListeners?.();
    this.cleanupListeners = null;
  }

  private sendFrame(frame: ReplayFrameInput, lifecycle: number, subject: string): void {
    if (lifecycle !== this.lifecycle || !subject || !this.statusValue.active) return;
    // No keepalive: frames carry the whole visible tree (up to 512 KiB plus
    // 32 KiB style elements), far above the Fetch keepalive 64 KiB budget, so
    // keepalive posts would fail silently inside the catch below.
    const work = (async () => {
      try {
        const response = await this.fetcher('/api/research/replay/frames', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'content-type': 'application/json', 'sec-fetch-site': 'same-origin', 'X-Research-Subject': subject },
          body: JSON.stringify({ frame }),
        });
        if (response.status !== 403 || lifecycle !== this.lifecycle) return;
        // Replay was stopped elsewhere (another device, a restore), or this account left the replay list: stop
        // sending instead of failing every second.
        const body = await response.json().catch(() => null) as { code?: unknown } | null;
        if ((body?.code === 'replay-inactive' || body?.code === 'forbidden') && lifecycle === this.lifecycle) this.endCapture();
      } catch { /* no queue or retry: an unavailable frame is reported as unavailable */ }
    })();
    this.pending.add(work);
    void work.finally(() => this.pending.delete(work));
  }

  private async flushPending(): Promise<void> { await Promise.allSettled([...this.pending]); }

  private async deleteServerParticipation(subject: string, lifecycle: number): Promise<boolean> {
    if (!subject) return false;
    try {
      const response = await this.fetcher('/api/research/replay/participation', {
        method: 'DELETE', credentials: 'same-origin', headers: { 'content-type': 'application/json', 'sec-fetch-site': 'same-origin', 'X-Research-Subject': subject }, body: '{}',
      });
      return response.ok && lifecycle === this.lifecycle;
    } catch { return false; }
  }

  private stopKey(subject: string): string { return `li-replay-v1:${subject}:stopped`; }
  private storageReady(): boolean {
    if (typeof window === 'undefined') return false;
    const probe = `li-replay-v1:${this.subject}:probe`;
    try {
      localStorage.setItem(probe, '1'); localStorage.removeItem(probe);
      sessionStorage.setItem(probe, '1'); sessionStorage.removeItem(probe);
      return true;
    } catch { return false; }
  }
  private hasLocalStop(subject: string): boolean {
    if (!this.storageReady()) return true;
    try { return localStorage.getItem(this.stopKey(subject)) === '1' || sessionStorage.getItem(this.stopKey(subject)) === '1'; }
    catch { return true; }
  }
  private markStopped(subject: string): void {
    try { localStorage.setItem(this.stopKey(subject), '1'); } catch { /* in-memory stop still applies */ }
    try { sessionStorage.setItem(this.stopKey(subject), '1'); } catch { /* in-memory stop still applies */ }
  }
  private clearLocalStop(subject: string): boolean {
    try {
      localStorage.removeItem(this.stopKey(subject));
      sessionStorage.removeItem(this.stopKey(subject));
      return true;
    } catch { return false; }
  }
  private broadcastStop(subject: string): void {
    if (!subject) return;
    try { this.channel?.postMessage({ type: 'stop', subjectId: subject }); } catch { /* local stop remains */ }
  }
  private dispatch(): void { if (typeof window !== 'undefined') window.dispatchEvent(new Event('li-replay-state')); }
}
